import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/affiliate-links")({
  component: AdminAffiliateLinksPage,
  head: () => ({
    meta: [
      { title: "Affiliate link audit | What Finland admin" },
      {
        name: "description",
        content:
          "Audit trail of outbound partner links: what was stored, what was opened, and whether affiliate tracking was applied.",
      },
    ],
  }),
});

type ClickRow = {
  id: string;
  created_at: string;
  kind: string;
  destination_url: string;
  source_url: string | null;
  affiliate_network: string | null;
  affiliate_applied: boolean;
  country: string | null;
  place_id: string;
};

type Filter = "all" | "affiliate" | "plain";

function AdminAffiliateLinksPage() {
  const [filter, setFilter] = useState<Filter>("affiliate");

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["admin", "affiliate-links", filter],
    queryFn: async () => {
      let q = supabase
        .from("partner_clicks")
        .select(
          "id, created_at, kind, destination_url, source_url, affiliate_network, affiliate_applied, country, place_id",
        )
        .order("created_at", { ascending: false })
        .limit(200);
      if (filter === "affiliate") q = q.eq("affiliate_applied", true);
      if (filter === "plain") q = q.eq("affiliate_applied", false);
      const { data, error } = await q;
      if (error) throw error;
      const rows = (data ?? []) as ClickRow[];

      const ids = Array.from(new Set(rows.map((r) => r.place_id)));
      let names = new Map<string, { name: string; slug: string }>();
      if (ids.length) {
        const { data: places } = await supabase
          .from("places")
          .select("id, name, slug")
          .in("id", ids);
        names = new Map(
          (places ?? []).map((p: any) => [p.id as string, { name: p.name, slug: p.slug }]),
        );
      }
      return rows.map((r) => ({ ...r, place: names.get(r.place_id) ?? null }));
    },
    staleTime: 30_000,
  });

  const rows = data ?? [];

  const stats = useMemo(() => {
    const affiliate = rows.filter((r) => r.affiliate_applied).length;
    return { total: rows.length, affiliate, plain: rows.length - affiliate };
  }, [rows]);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link
            to="/admin/$page"
            params={{ page: "dashboard" }}
            className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Admin
          </Link>
          <h1 className="font-heading text-2xl font-semibold">Affiliate link audit</h1>
          <p className="text-sm text-muted-foreground">
            Every outbound partner click: the link we stored, the final URL the guest opened, and
            whether affiliate tracking was added.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted"
        >
          {isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Refresh
        </button>
      </div>

      <div className="mb-4 grid grid-cols-3 gap-3">
        {[
          { label: "Clicks shown", value: stats.total },
          { label: "Affiliate tagged", value: stats.affiliate },
          { label: "Other links", value: stats.plain },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-border bg-card p-4">
            <div className="text-2xl font-semibold">{s.value}</div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {(
          [
            { id: "affiliate", label: "Affiliate tracked" },
            { id: "plain", label: "Not tracked" },
            { id: "all", label: "All clicks" },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setFilter(t.id)}
            className={`rounded-full border px-4 py-1.5 text-sm transition ${
              filter === t.id
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-card hover:bg-muted"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 p-8 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
          No clicks recorded yet for this filter.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border border-border bg-card p-4">
              <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">{r.place?.name ?? "Unknown place"}</span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs uppercase tracking-wide">
                  {r.kind}
                </span>
                {r.affiliate_applied ? (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                    {r.affiliate_network ?? "affiliate"} tracked
                  </span>
                ) : null}
                {r.country ? (
                  <span className="text-xs text-muted-foreground">{r.country}</span>
                ) : null}
                <span className="ml-auto text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleString()}
                </span>
              </div>
              <dl className="space-y-1 text-xs">
                <div className="flex gap-2">
                  <dt className="w-20 shrink-0 text-muted-foreground">Saved link</dt>
                  <dd className="break-all">{r.source_url ?? "—"}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-20 shrink-0 text-muted-foreground">Opened</dt>
                  <dd className="break-all">
                    <a
                      href={r.destination_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-start gap-1 text-primary hover:underline"
                    >
                      {r.destination_url}
                      <ExternalLink className="mt-0.5 h-3 w-3 shrink-0" />
                    </a>
                  </dd>
                </div>
              </dl>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
